<svelte:options customElement="hb-checkout" />

<script lang="ts">
  /**
   * Svelte Payment Page WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import "@google-pay/button-element";

  import pkg from "../../../package.json";
  import type {
    IShipment,
    IUser,
    IGateway,
    IPayment,
  } from "./types/webcomponent.type";
  import type { FormSchema } from "../form/types/webcomponent.type";
  import type { Events as PaymentPaypalEvents } from "../payment-paypal/types/webcomponent.type";
  import { formUserSchema, formCreditCardSchema } from "./libs/formSchemes";
  import dayjs from "dayjs";
  import {
    addComponent,
    getChildStyleToPass,
    LanguageTranslator,
  } from "wc-js-utils";
  import parseStyle from "style-to-object";
  import { styleSetup as formStyleSetup } from "../form/extra/docs";
  import { styleSetup as paymentPaypalStyleSetup } from "../payment-paypal/extra/docs";
  import type { Component } from "./types/webcomponent.type";

  let formStyleToSet: string = $state("");
  let paymentPaypalStyleToSet: string = $state("");
  let parsedStyle: { [x: string]: string } = $state({});

  // import debounce from "debounce";
  // import { printInvoice, OpenInvoiceWindow } from "../../../page-invoice/extra/utils";
  const nullishdefaultpayment: IPayment = {
    type: "plain",
    total: 0,
    currencyCode: "EUR",
    countryCode: "IT",
    merchantName: "merchant",
  };

  let {
    id = "",
    // style = undefined,
    shipments = [],
    user = undefined,
    gateways = [],
    payment = nullishdefaultpayment,
    completed = "no",
  }: Component = $props();

  // let gateway: IGateway;
  const formShipmentSchema: FormSchema = [
    {
      type: "radio",
      placeholder: "Insert your Full Name here...",
      id: "shipmentsolution",
      required: true,
      label: "Shipment",
      validationTip: "This field cannot be empty.",
      params: {
        options: [],
      },
    },
  ];

  let editUser: boolean = $state(false);
  let editShipping: boolean = $state(false);

  let formUserSchemaSubmitted: "yes" | "no" = $state("no");
  let formShipmentSchemaSubmitted: "yes" | "no" = $state("no");
  let formCreditCardSchemaSubmitted: "yes" | "no" = $state("no");

  $effect(() => {
    // if (style) {
    //   parsedStyle = parseStyle(style) as any;
    //   formStyleToSet = getChildStyleToPass(parsedStyle, formStyleSetup?.vars);
    //   paymentPaypalStyleToSet = getChildStyleToPass(
    //     parsedStyle,
    //     paymentPaypalStyleSetup?.vars
    //   );
    // }
    if (typeof payment === "string")
      payment = JSON.parse(payment) || nullishdefaultpayment;

    if (typeof shipments === "string") shipments = JSON.parse(shipments) || [];
    if (typeof gateways === "string") {
      gateways = JSON.parse(gateways) || [];
      for (const g of gateways) {
        if (!g.cardNetworks?.length) g.cardNetworks = ["VISA", "MASTERCARD"];
      }
      // if (gateways.find((f) => f.selected || f.default)) {
      // 	gateway = gateways.find((f) => f.selected || f.default);
      // } else if (gateways?.length) {
      // 	gateway = gateways[0];
      // }
    }
    if (!payment.type) payment.type = "buy";
    if (typeof user === "string") {
      user = JSON.parse(user);
      if (user) {
        for (const k of Object.keys(user)) {
          if (formUserSchema?.find?.((f) => f.id === k)) {
            formUserSchema[
              formUserSchema?.findIndex?.((f) => f.id === k)
            ].value = user[k as keyof IUser];
          } else if (
            formUserSchema?.find?.(
              (f) =>
                f.params?.columns?.length &&
                f.params.columns?.find?.((ff: any) => ff.id === k),
            )
          ) {
            const rowIndex = formUserSchema?.findIndex?.(
              (f) =>
                f.params?.columns?.length &&
                f.params.columns.find((ff: any) => ff.id === k),
            );
            const elIndex = formUserSchema[
              rowIndex
            ].params?.columns?.findIndex?.((ff: any) => ff.id === k);
            if (formUserSchema[rowIndex].params && (elIndex || elIndex === 0))
              formUserSchema[rowIndex].params.columns[elIndex].value =
                user[k as keyof IUser];
          }
        }
      }
    }

    if (formShipmentSchema?.[0]?.params) {
      formShipmentSchema[0].params.options = shipments.map((m) => {
        return { value: m.id, label: m.label + " " + m.price + m.currency };
      });
    }

    if (
      formShipmentSchema[0] &&
      shipments &&
      (shipments.find((f) => f.selected) ||
        shipments.find((f) => f.standard)) &&
      (shipments.find((f) => f.selected) || shipments.find((f) => f.standard))
        ?.id
    ) {
      formShipmentSchema[0].value = (
        shipments.find((f) => f.selected) || shipments.find((f) => f.standard)
      )?.id;
    }
  });

  function cardChange(u: {
    _valid: boolean;
    fullName?: string;
    cardNumber?: string;
    CVV?: string;
    expiration?: string;
  }) {
    for (const k of ["fullName", "cardNumber", "CVV", "expiration"]) {
      if (formCreditCardSchema.find((f) => f.id === k)) {
        formCreditCardSchema[
          formCreditCardSchema.findIndex((f) => f.id === k)
        ].value = u[k as "fullName" | "cardNumber" | "CVV" | "expiration"];
      } else if (
        formCreditCardSchema.find(
          (f) =>
            f.params?.columns?.length &&
            f.params.columns.find((ff: any) => ff.id === k),
        )
      ) {
        const rowIndex = formCreditCardSchema.findIndex(
          (f) =>
            f.params?.columns?.length &&
            f.params.columns.find((ff: any) => ff.id === k),
        );
        const elIndex = formCreditCardSchema[
          rowIndex
        ].params?.columns?.findIndex((ff: any) => ff.id === k);
        if (elIndex)
          (formCreditCardSchema as any)[rowIndex].params.columns[
            elIndex
          ].value = u[k as "fullName" | "cardNumber" | "CVV" | "expiration"];
      }
    }
  }
  function saveUser(userInputs: {
    fullName: string;
    addressWithNumber: string;
    city: string;
    zip: string;
    nationality: string;
  }) {
    console.log("saveuser", userInputs);
    const newUser: IUser = {
      fullName: userInputs.fullName,
      addressWithNumber: userInputs.addressWithNumber,

      city: userInputs.city,
      zip: userInputs.zip,
      nationality: userInputs.nationality,
    };
    for (const k of Object.keys(userInputs)) {
      if (formUserSchema.find((f) => f.id === k)) {
        formUserSchema[formUserSchema.findIndex((f) => f.id === k)].value =
          userInputs[
            k as keyof {
              fullName: string;
              addressWithNumber: string;
              city: string;
              zip: string;
              nationality: string;
            }
          ];
      } else if (
        formUserSchema.find(
          (f) =>
            f.params?.columns?.length &&
            f.params.columns.find((ff: any) => ff.id === k),
        )
      ) {
        const rowIndex = formUserSchema.findIndex(
          (f) =>
            f.params?.columns?.length &&
            f.params.columns.find((ff: any) => ff.id === k),
        );
        const elIndex = formUserSchema[rowIndex].params?.columns.findIndex(
          (ff: any) => ff.id === k,
        );
        if (elIndex)
          (formUserSchema as any)[rowIndex].params.columns[elIndex].value =
            userInputs[
              k as keyof {
                fullName: string;
                addressWithNumber: string;
                city: string;
                zip: string;
                nationality: string;
              }
            ];
      }
    }
    user = newUser;
    editUser = false;
    formUserSchemaSubmitted = "no";
    console.log("edited");
    dispatch("saveUser", user);
  }

  function saveShipment(results: { shipmentsolution: string }) {
    console.log("saveShipment", results);

    const shipmentId = results?.shipmentsolution;
    if (shipmentId && shipments.find((f) => f.id === shipmentId)) {
      shipments.forEach((f) => (f.selected = false));
      const i = shipments.findIndex((f) => f.id === shipmentId);
      shipments[i].selected = true;
      editShipping = false;
      formShipmentSchema[0].value = shipmentId;
      dispatch("saveShipment", shipments[i]);
    }

    formShipmentSchemaSubmitted = "no";
  }

  // function payByPaypalAndCard(p: {}) {
  // 	formCreditCardSchemaSubmitted = "no";

  // 	dispatch("payByCard", p);
  // }

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  addComponent({ repoName: "@htmlbricks/hb-form", version: pkg.version });
  addComponent({
    repoName: "@htmlbricks/hb-payment-paypal",
    version: pkg.version,
  });

  function editUserForm() {
    editUser = true;
    editShipping = false;
  }
  function editShippingForm() {
    editUser = false;

    editShipping = true;
  }

  // function selectGateway(id: string) {
  // 	if (gateways.find((f) => f.id === id)) {
  // 		gateway = gateways.find((f) => f.id === id);
  // 		dispatch("setGateway", gateway);
  // 	}
  // }

  function paymentCompleted(method: string) {
    completed = "yes";
    dispatch("paymentCompleted", {
      total: payment?.total,
      method,
      completed: true,
    });
  }
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/bootstrap-icons@latest/font/bootstrap-icons.css"
  />
</svelte:head>
<slot name="title"><h1 class="title" part="title">Checkout</h1></slot>
<div id="border_top">
  <div>
    <h3 class="subtitle" part="subtitle">
      <i class="bi bi-send-fill"></i> Delivery
    </h3>

    {#if user?.fixed && !editUser}
      <div class="dataentrycontainer">
        <slot name="userinfo">
          {#if user.fullName}
            <div class="dataentry">
              <span class="dataentry_value">{user.fullName}</span>
            </div>
          {/if}
        </slot>
      </div>
    {:else if user?.fullName && user?.addressWithNumber && !editUser}
      <h4 class="subtitle" part="subtitle">
        <button class="btn btn-sm detail_btn" onclick={editUserForm}
          >edit</button
        >
      </h4>

      <div class="dataentrycontainer">
        <div class="dataentry">
          <span class="dataentry_key">Name</span><span class="dataentry_value"
            >{user.fullName}</span
          >
        </div>
        <div class="dataentry">
          <span class="dataentry_key">Address</span><span
            class="dataentry_value"
            >{`${user.addressWithNumber}, ${user.city}, ${user.zip},${user.nationality}`}</span
          >
        </div>
      </div>
    {:else}
      <h4 class="subtitle" part="subtitle">Address</h4>

      <div>
        <hb-form
          hide_submit="yes"
          style={formStyleToSet}
          schema={JSON.stringify(formUserSchema)}
          submitted={formUserSchemaSubmitted}
          onsubmit={(e: { detail: any }) => {
            saveUser(e.detail);
          }}
        ></hb-form>
        <div>
          <button
            onclick={() => {
              formUserSchemaSubmitted = "yes";
              setTimeout(() => {
                formUserSchemaSubmitted = "no";
              }, 200);
            }}
            style="width:100%;margin-bottom:10px"
            class="btn btn-primary">continue</button
          >
        </div>
      </div>
    {/if}
  </div>
  {#if shipments?.length}
    <div id="shipment_separator">
      {#if ((user?.fullName && user?.addressWithNumber) || user?.fixed) && !editUser}
        {#if !editShipping && typeof shipments === "object" && shipments?.length && (shipments.find((f) => f.selected) || shipments.find((f) => f.standard))}
          <h4 style="margin-top:20px" class="subtitle" part="subtitle">
            <span class="detail_label"
              ><i class="bi bi-truck"></i> Shipment Service</span
            ><button class="btn btn-sm detail_btn" onclick={editShippingForm}
              >edit</button
            >
          </h4>
          <div class="dataentrycontainer">
            <div class="dataentry">
              <span class="dataentry_key">Shipping Fee</span><span
                class="dataentry_value"
                >{(
                  shipments.find((f) => f.selected) ||
                  shipments.find((f) => f.standard)
                )?.price}</span
              >
            </div>
            <div class="dataentry">
              <span class="dataentry_key">Arrive</span><span
                class="dataentry_value"
                >{dayjs(
                  (
                    shipments.find((f) => f.selected) ||
                    shipments.find((f) => f.standard)
                  )?.arriveDate,
                ).format()}
              </span>
            </div>
          </div>
        {:else}
          <h4 class="subtitle" part="subtitle" style="margin-top:20px">
            <i class="bi bi-truck"></i> Shipment Service
          </h4>
          <div>
            <hb-form
              hide_submit="yes"
              style={formStyleToSet}
              schema={JSON.stringify(formShipmentSchema)}
              submitted={formShipmentSchemaSubmitted}
              onsubmit={(e: { detail: any }) => {
                saveShipment(e.detail);
              }}
            ></hb-form>
            <div>
              <button
                onclick={() => {
                  formShipmentSchemaSubmitted = "yes";
                  setTimeout(() => {
                    formShipmentSchemaSubmitted = "no";
                  }, 200);
                }}
                style="width:100%"
                class="btn btn-primary">continue</button
              >
            </div>
          </div>
        {/if}
      {:else if !editShipping && shipments && typeof shipments === "object" && (shipments.find((f) => f.selected) || shipments.find((f) => f.standard))}
        <h4 style="margin-top:20px" class="subtitle" part="subtitle">
          <span class="detail_label"
            ><i class="bi bi-truck"></i> Shipment Service</span
          ><button class="btn btn-sm detail_btn" onclick={editShippingForm}
            >edit</button
          >
        </h4>
        <div class="dataentrycontainer">
          <div class="dataentry">
            <span class="dataentry_key">Shipping Fee</span><span
              class="dataentry_value"
              >{(
                shipments.find((f) => f.selected) ||
                shipments.find((f) => f.standard)
              )?.price}</span
            >
          </div>
          <div class="dataentry">
            <span class="dataentry_key">Shipping Time</span><span
              class="dataentry_value"
              >{(
                shipments.find((f) => f.selected) ||
                shipments.find((f) => f.standard)
              )?.arriveDate}</span
            >
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>
<div class="payment_title">
  {#if completed !== "yes"}
    <h3 class="subtitle" part="subtitle">
      <i class="bi bi-wallet2"></i> Payment Method
    </h3>
    {#if !editUser && !editShipping && (shipments?.find?.((f) => f.selected) || shipments?.find?.((f) => f.standard) || !shipments?.length)}
      <!-- {#if gateways.length > 1}
			<div id="card_select">
				{#each gateways as g (g.id)}
					<img onclick={() => selectGateway(g.id)} class="cardimg" alt={g.label} src={g.cardImage} />
				{/each}
			</div>
		{/if} -->
      <div id="payment_btn_container">
        {#each gateways as g (g.id)}
          <div class="payment_button_container">
            {#if g.id === "paypal"}
              <hb-payment-paypal
                style={paymentPaypalStyleToSet}
                class="payment_button"
                paypalid={g.paypalid}
                total={payment?.total?.toString()}
                onpaymentCompleted={(e: {
                  detail: PaymentPaypalEvents["paymentCompleted"];
                }) => paymentCompleted("paypal")}
              ></hb-payment-paypal>
            {:else if g.id === "google" && payment.countryCode}
              <google-pay-button
                class="payment_button"
                environment="TEST"
                button-type={payment?.type?.toLowerCase()}
                button-color="black"
                button-size-mode="fill"
                paymentRequest={{
                  apiVersion: 2,
                  apiVersionMinor: 0,
                  merchantInfo: {
                    merchantName: payment.merchantName,
                    merchantId: g.gatewayMerchantId,
                  },
                  allowedPaymentMethods: [
                    {
                      type: "CARD",
                      parameters: {
                        allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                        allowedCardNetworks: g.cardNetworks,
                      },
                      tokenizationSpecification: {
                        type: "PAYMENT_GATEWAY",
                        parameters: {
                          gateway: g.gatewayId,
                          gatewayMerchantId: g.gatewayMerchantId,
                        },
                      },
                    },
                    // {
                    // 	type: "PAYPAL",
                    // 	parameters: {
                    // 		purchase_context: {
                    // 			purchase_units: [
                    // 				{
                    // 					payee: {
                    // 						merchant_id: "PAYPAL_ACCOUNT_ID",
                    // 					},
                    // 				},
                    // 			],
                    // 		},
                    // 	},
                    // 	tokenizationSpecification: {
                    // 		type: "DIRECT",
                    // 	},
                    // },
                  ],
                  transactionInfo: {
                    totalPriceStatus: "FINAL",
                    totalPriceLabel: "Total",
                    totalPrice: payment?.total?.toString(),
                    currencyCode: payment?.currencyCode?.toUpperCase(),
                    countryCode: payment?.countryCode?.toUpperCase(),
                    checkoutOption: "COMPLETE_IMMEDIATE_PURCHASE",
                  },
                }}
                onloadpaymentdata={(event: any) => {
                  paymentCompleted("google");
                }}
              ></google-pay-button>
            {/if}
          </div>
        {/each}
      </div>

      <!-- <div>
			<button onclick={() => paymentCompleted()} style="width:100%;background-color:yellow;color:white" class="btn">paypal</button>
		</div>
		<div class="utils_or"><span>or</span></div>
		<div>
			<div>
				<hb-form
					schema={JSON.stringify(formCreditCardSchema)}
					submitted={formCreditCardSchemaSubmitted}
					onsubmit={(e) => payByCard(e.detail)}
					onchange={(e) => {
						cardChange(e.detail);
					}}
				/>
			</div>
			<div>
				<button
					onclick={() => {
						formCreditCardSchemaSubmitted = "yes";
						setTimeout(() => {
							formCreditCardSchemaSubmitted = "no";
						}, 200);
					}}
					style="width:100%"
					class="btn btn-primary">place order</button
				>
			</div>
		</div> -->
    {/if}
    <div class="footer_note">
      <slot name="payment_terms"
        >By Clicking..<button class="btn btn-default"
          >Terms and conditions</button
        ></slot
      >
    </div>
  {:else}
    <slot name="payment_completed">
      <div id="payment_completed_content">
        <div id="payment_completed_title">payment completed</div>
        <div id="payment_completed_content">Total: {payment.total}</div>
        <div id="payment_completed_footer">
          <button class="btn btn-link">invoice</button>
        </div>
      </div>
    </slot>
  {/if}
</div>

<style lang="scss">
  @forward "./styles/bootstrap.scss";
  @forward "./styles/webcomponent.scss";
  #payment_completed_content {
    text-align: center;
  }
  #payment_completed_title {
    margin: 30px;
    text-transform: capitalize;
    font-size: 2em;
    font-weight: bold;
  }
  #payment_btn_container {
    display: flex;
  }
  .payment_button_container {
    flex: 1;
    margin: 15px;
    display: inline-block;
    text-align: center;
  }
  .payment_button {
    height: 40px;
    width: 100%;
    max-width: 50vw;
  }

  .utils_or {
    width: 50%;
    text-align: center;
    border-bottom: var(--hb-checkout-border);
    line-height: 0.1em;
    margin: 40px auto 30px auto;
  }
  .utils_or span {
    background: #fff;
    padding: 0 10px;
  }

  .detail_label {
    font-size: 0.8em;
    font-weight: bold;
  }
  .dataentry_key {
    margin-right: 3px;
  }
  .dataentry_key::after {
    content: ":";
  }
  .dataentry_relevant_value {
    font-weight: bold;
  }
  .detail_btn {
    float: right;
    margin-right: 10px;
    color: var(--edit-color);
    font-weight: bold;
    text-transform: capitalize;
  }
  // #border_top {
  // 	border-top: var(--hb-checkout-border);
  // 	border-bottom: var(--hb-checkout-border);
  // 	margin-top: 40px;
  // }
  .dataentry {
    line-height: 30px;
  }
  .footer_note {
    text-align: center;
    font-size: 12px;
    padding: 5px;
    line-height: 30px;
  }
  .subtitle {
    margin-bottom: 30px;
  }
  .title {
    text-align: center;
    border-bottom: var(--hb-checkout-border);
    padding-bottom: 20px;
  }

  .payment_title {
    margin-top: 20px;
    border-top: var(--hb-checkout-border);
    padding-top: 10px;
  }

  // .cardimg {
  // 	max-width: 60px;
  // 	margin: auto 2px auto 2px;
  // }
  // #card_select {
  // 	margin: 20px auto 40px auto;
  // }
</style>
